// eslint-disable-next-line no-restricted-imports
import Fuse from 'fuse.js'
// eslint-disable-next-line no-restricted-imports
import type { IFuseOptions } from 'fuse.js'

import type { Service } from '#ourahub-intra/types/services'

export interface SearchableService extends Service {
  tags: string[]
}

type SearchField = 'id' | 'name' | 'tags' | 'summary' | 'category' | 'description'

const KEYS: { name: SearchField; weight: number }[] = [
  { name: 'name', weight: 5 },
  { name: 'tags', weight: 3 },
  { name: 'summary', weight: 2 },
  { name: 'category', weight: 2 },
  { name: 'description', weight: 1 },
  { name: 'id', weight: 1 },
]

const FUSE_OPTIONS: IFuseOptions<SearchableService> = {
  includeScore: true,
  ignoreLocation: true,
  threshold: 0.2,
  keys: KEYS,
}

function parseQuery(query: string): { phrases: string[]; rest: string } {
  const phrases: string[] = []

  const rest = query.replace(/"([^"]+)"/g, (_, phrase: string) => {
    phrases.push(phrase.trim())

    return ' '
  })

  return { phrases: phrases.filter(Boolean), rest: rest.trim() }
}

function fieldValues(service: SearchableService): string[] {
  return KEYS.flatMap(({ name }) => {
    const value = service[name]

    return Array.isArray(value) ? value : [String(value)]
  })
}

function includesPhrase(service: SearchableService, phrase: string): boolean {
  const needle = phrase.toLowerCase()

  return fieldValues(service).some((value) => value.toLowerCase().includes(needle))
}

let indexCache: { services: SearchableService[]; fuse: Fuse<SearchableService> } | null = null

function getFuse(services: SearchableService[]): Fuse<SearchableService> {
  if (indexCache?.services !== services) {
    indexCache = { services, fuse: new Fuse(services, FUSE_OPTIONS) }
  }

  return indexCache.fuse
}

// Every whitespace term must match (AND), ranked by summed relevance so that better matches rank higher.
function rankByTerms(fuse: Fuse<SearchableService>, terms: string[]): SearchableService[] {
  let matched: Map<string, { item: SearchableService; relevance: number }> | null = null

  for (const term of terms) {
    const hits = new Map(fuse.search(term).map((r) => [r.item.id, { item: r.item, relevance: 1 - (r.score ?? 1) }]))

    if (!matched) {
      matched = hits
      continue
    }

    for (const [id, current] of matched) {
      const hit = hits.get(id)

      if (hit) current.relevance += hit.relevance
      else matched.delete(id)
    }
  }

  return [...(matched?.values() ?? [])].sort((a, b) => b.relevance - a.relevance).map((r) => r.item)
}

export function searchServices(services: SearchableService[], query: string): SearchableService[] {
  const { phrases, rest } = parseQuery(query.trim())

  if (!phrases.length && !rest) return services

  const passesPhrases = (service: SearchableService) => phrases.every((phrase) => includesPhrase(service, phrase))

  const terms = rest.split(/\s+/).filter(Boolean)

  if (!terms.length) return services.filter(passesPhrases)

  const ranked = rankByTerms(getFuse(services), terms)

  return phrases.length ? ranked.filter(passesPhrases) : ranked
}
